import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { NavItem } from 'src/app/layouts/full/vertical/sidebar/nav-item/nav-item';
import {
  defaultTenantRouteForModule,
  moduleHasSubnav,
  routesForModule,
} from 'src/app/layouts/full/vertical/sidebar/sidebar-data';
import { FormSaveBarOverlayService } from '@shared/ui/milinco-form-save-bar/form-save-bar-overlay.service';
import { AdminKeyboardInsetService } from './admin-keyboard-inset.service';
import {
  MilincoMobileDockLauncherComponent,
  MobileDockLauncherItem,
} from './milinco-mobile-dock-launcher.component';
import { NavIconPairComponent } from '@shared/ui/nav-icon-pair/nav-icon-pair.component';

export interface MobileDockMenuItem {
  id: number;
  title: string;
  iconName: string;
}

const HUB_PREFERRED_IDS = [1, 2, 4] as const;
/** Umbral px para considerar overflow / borde de scroll. */
const SCROLL_EDGE_PX = 2;

@Component({
  selector: 'app-milinco-mobile-dock',
  standalone: true,
  imports: [TranslateModule, RouterLink, RouterLinkActive, NavIconPairComponent],
  templateUrl: './milinco-mobile-dock.component.html',
  styleUrl: './milinco-mobile-dock.component.scss',
  host: {
    class: 'milinco-mobile-dock-host',
    '[class.milinco-mobile-dock-host--hidden]': 'isHidden()',
    '[attr.aria-hidden]': 'isHidden() ? true : null',
  },
})
export class MilincoMobileDockComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly router = inject(Router);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly translate = inject(TranslateService);
  private readonly saveBarOverlay = inject(FormSaveBarOverlayService);
  private readonly keyboardInset = inject(AdminKeyboardInsetService);
  private routerSub?: Subscription;
  private detachKeyboard?: () => void;
  private sectionsResizeObserver?: ResizeObserver;
  private lastWorkshopLinkCount = -1;

  readonly sectionsEl = viewChild<ElementRef<HTMLElement>>('sectionsEl');

  readonly menuItems = input<MobileDockMenuItem[]>([]);
  readonly navItems = input<NavItem[]>([]);
  readonly selectedModuleId = input<number | null>(null);

  readonly moduleSelected = output<number>();

  private readonly currentUrl = signal(this.router.url.split('?')[0]);

  readonly keyboardOpen = computed(() => this.keyboardInset.bottomInsetPx() > 0);
  readonly saveBarShowing = this.saveBarOverlay.showing;

  readonly isHidden = computed(() => this.keyboardOpen() || this.saveBarShowing());

  /**
   * Taller: módulo actual con ≥2 secciones.
   * Secciones a la izquierda (repartidas o scrolleables); Menú fijo a la derecha.
   */
  readonly isWorkshop = computed(() =>
    moduleHasSubnav(this.navItems(), this.selectedModuleId())
  );

  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);
  /** Hay overflow real → mostrar flechas de scroll. */
  readonly sectionsOverflow = signal(false);

  readonly hubSlots = computed((): MobileDockMenuItem[] => {
    const items = this.menuItems();
    const byId = new Map(items.map((item) => [item.id, item]));
    const slots: MobileDockMenuItem[] = [];
    for (const id of HUB_PREFERRED_IDS) {
      const hit = byId.get(id);
      if (hit) {
        slots.push(hit);
      }
      if (slots.length >= 3) {
        break;
      }
    }
    if (slots.length < 3) {
      for (const item of items) {
        if (slots.some((s) => s.id === item.id)) {
          continue;
        }
        slots.push(item);
        if (slots.length >= 3) {
          break;
        }
      }
    }
    return slots;
  });

  /** Columnas del hub = slots + Menú (reparto estético del ancho). */
  readonly hubColumnCount = computed(() => this.hubSlots().length + 1);

  readonly workshopLinks = computed(() => {
    const moduleId = this.selectedModuleId();
    if (moduleId == null) {
      return [];
    }
    const module = this.navItems().find((m) => m.id === moduleId);
    const children = module?.children ?? [];
    return children
      .filter(
        (child): child is NavItem & { route: string; displayName: string } =>
          !!child.route?.trim() &&
          !!child.displayName?.trim() &&
          !child.openPosInNewTab &&
          !child.openInNewTab
      )
      .map((child) => ({
        route: child.route.startsWith('/') ? child.route : `/${child.route}`,
        label: child.displayName,
        iconName: child.iconName ?? 'solar:widget-2-line-duotone',
      }));
  });

  readonly launcherItems = computed((): MobileDockLauncherItem[] => {
    const hubIds = new Set(this.hubSlots().map((s) => s.id));
    return this.menuItems()
      .filter((item) => !hubIds.has(item.id))
      .map((item) => ({
        id: item.id,
        title: item.title,
        iconName: item.iconName,
      }));
  });

  constructor() {
    afterNextRender(() => {
      this.bindSectionsObserver();
      this.updateScrollHints();
    });
  }

  ngOnInit(): void {
    this.detachKeyboard = this.keyboardInset.attach();
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.currentUrl.set(e.urlAfterRedirects.split('?')[0]);
        queueMicrotask(() => this.updateScrollHints());
      });
    document.documentElement.style.setProperty('--milinco-bottom-chrome', '4.25rem');
  }

  ngAfterViewChecked(): void {
    const count = this.workshopLinks().length;
    if (count !== this.lastWorkshopLinkCount) {
      this.lastWorkshopLinkCount = count;
      this.bindSectionsObserver();
      this.updateScrollHints();
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.detachKeyboard?.();
    this.sectionsResizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--milinco-bottom-chrome');
  }

  /** Un toque = ir al módulo (ruta por defecto). Sin sheet intermedio de secciones. */
  selectHub(item: MobileDockMenuItem): void {
    this.moduleSelected.emit(item.id);
    const route =
      defaultTenantRouteForModule(this.navItems(), item.id) ??
      routesForModule(this.navItems(), item.id)[0] ??
      null;
    if (route) {
      void this.router.navigateByUrl(route);
    }
  }

  openMore(): void {
    const items = this.launcherItems();
    const hubAlso = this.hubSlots().map((item) => ({
      id: item.id,
      title: item.title,
      iconName: item.iconName,
    }));
    const all = [...hubAlso, ...items];
    const ref = this.bottomSheet.open(MilincoMobileDockLauncherComponent, {
      data: {
        items: all,
        titleKey: 'shell.dock.moreTitle',
        activeId: this.selectedModuleId(),
      },
      ariaLabel: this.translate.instant('shell.dock.moreTitle'),
      panelClass: ['milinco-admin-bottom-sheet', 'milinco-mobile-dock-launcher-pane'],
    });
    ref.afterDismissed().subscribe((id) => {
      if (id == null) {
        return;
      }
      const item = all.find((entry) => entry.id === id);
      if (item) {
        this.selectHub(item);
      }
    });
  }

  isHubActive(item: MobileDockMenuItem): boolean {
    return this.selectedModuleId() === item.id;
  }

  onSectionsScroll(): void {
    this.updateScrollHints();
  }

  scrollSections(direction: -1 | 1): void {
    const el = this.sectionsEl()?.nativeElement;
    if (!el) {
      return;
    }
    const delta = Math.max(el.clientWidth * 0.75, 120) * direction;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }

  private bindSectionsObserver(): void {
    this.sectionsResizeObserver?.disconnect();
    const el = this.sectionsEl()?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.sectionsResizeObserver = new ResizeObserver(() => this.updateScrollHints());
    this.sectionsResizeObserver.observe(el);
  }

  updateScrollHints(): void {
    const el = this.sectionsEl()?.nativeElement;
    if (!el) {
      this.canScrollLeft.set(false);
      this.canScrollRight.set(false);
      this.sectionsOverflow.set(false);
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    const overflow = maxScroll > SCROLL_EDGE_PX;
    this.sectionsOverflow.set(overflow);
    this.canScrollLeft.set(overflow && el.scrollLeft > SCROLL_EDGE_PX);
    this.canScrollRight.set(overflow && el.scrollLeft < maxScroll - SCROLL_EDGE_PX);
  }
}
